import type { useReportFilters } from '@/composables/useReportFilters';
import { getReportRequestLink } from '@/views/reports/report-params-build';
import { pushReportUrl } from '@/views/reports/report-route-push';
import {
  createReportFiltersHandlers,
  type ReportFiltersSortingState,
} from '@/views/reports/report-filters-runtime';

export type ReportFiltersApiSource = {
  getType: () => string;
  getGrouping: () => string | null | undefined;
  getSelectedMetrics: () => string[];
  getRouteName: () => string | symbol | null | undefined;
};

export type ReportFiltersBindingsSource = ReportFiltersApiSource & {
  getIsDashboard: () => boolean;
  getSubReport: () => boolean;
  filterPromise: Promise<unknown>;
  clearFilter: () => void;
  setFiltersActive: (active: boolean) => void;
  getPushUrlContext: () => Parameters<typeof pushReportUrl>[1];
};

export const createReportFiltersApi = (
  reportFilters: ReturnType<typeof useReportFilters>,
  getSource: () => ReportFiltersApiSource,
) => {
  return {
    reportQuery: () => reportFilters.reportQuery(),
    getIsDashboard: () => reportFilters.isDashboard.value,
    getBasicType: () => reportFilters.basicType.value,
    getCurrentReportType: () => reportFilters.currentReportType.value,
    isReport: () => reportFilters.isReport.value,
    getGroupByMetric: (metric: string) => reportFilters.getGroupByMetric(metric),
    drilldownForward: (
      previousFilter: unknown = null,
      nextReportProps: Parameters<typeof reportFilters.drilldownForward>[1] = {},
    ) => reportFilters.drilldownForward(previousFilter, nextReportProps),
    getReportFilters: () => reportFilters.getReportFilters(),
    getGroupBy: () => reportFilters.resolveGroupBy(getSource().getType()),
    getInitialFilters: () => reportFilters.getInitialFilters({
      grouping: getSource().getGrouping(),
      type: getSource().getType(),
    }),
    getFilters: () => reportFilters.getFilters({
      grouping: getSource().getGrouping(),
      type: getSource().getType(),
    }),
    getRequestLink: () => getReportRequestLink(getSource().getRouteName()),
    verifyQuery: (query: Record<string, unknown>, treeLevel: number) => {
      return reportFilters.verifyReportQueryForSelection(
        query,
        treeLevel,
        getSource().getSelectedMetrics(),
      );
    },
  };
};

export const createReportFiltersBindings = (
  reportFilters: ReturnType<typeof useReportFilters>,
  sortingFilters: ReportFiltersSortingState,
  getSource: () => ReportFiltersBindingsSource,
) => {
  let filtersHandlers: ReturnType<typeof createReportFiltersHandlers> | null = null;

  const getFiltersHandlers = () => {
    if (!filtersHandlers) {
      const source = getSource();
      filtersHandlers = createReportFiltersHandlers({
        isDashboard: source.getIsDashboard(),
        subReport: source.getSubReport(),
        filterPromise: source.filterPromise,
        clearFilter: source.clearFilter,
        getRouteName: source.getRouteName,
        getFilters: () => reportFilters.getFilters({
          grouping: source.getGrouping(),
          type: source.getType(),
        }),
        getReportParams: reportFilters.getReportParams,
        onFiltersActive: () => source.setFiltersActive(true),
      }, sortingFilters);
    }

    return filtersHandlers;
  };

  const filtersApi = createReportFiltersApi(reportFilters, getSource);

  return {
    ...filtersApi,
    setSorting: () => getFiltersHandlers().setSorting(),
    runBeforeMount: () => getFiltersHandlers().beforeMount(),
    pushURL: () => getFiltersHandlers().pushURL(getSource().getPushUrlContext()),
  };
};
